import { colorValue } from '@hai/ui-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { SortType } from 'src/js/component/sort-manager';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { fetchEssences } from 'src/js/pages/streaming/essence/essence-store';
import { StreamingActionBar } from 'src/js/component/actionBar/streaming-action-bar';
import { useWatchObject } from 'src/js/store/use-watch';
import { PRODUCT, constant } from 'src/js/constant';

import { fetchVideoInputs } from '../video-input/video-input-store';
import { VidencList } from './video-encoder-list';
import {
  VideoEncoderViewModel,
  encoderIsStarted,
  encoderIsStopped,
  encoderModelToStatus,
} from './video-encoder-models';
import encoders, { fetchResolutions, fetchVideoEncoders, startStopVideoEncoder } from './video-encoder-store';
import { useLocalStorage } from 'usehooks-ts';
import { fetchStreams } from '../stream/stream-store';

let connectionLost = false;
export const VideoEncodersPage: React.FunctionComponent = (props) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  // state management BEGINs
  const [videoEncoders, setVideoEncoders] = useState(encoders);
  const [isLoading, setIsLoading] = useState(true);
  useWatchObject(encoders, setVideoEncoders);

  const interval = constant.defaultRefreshInterval;
  useAdaptiveFetch(
    () =>
      fetchVideoEncoders(t).then((res) => {
        setIsLoading(false);
        if (res.ok) {
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    interval,
  );

  useAdaptiveFetch(() => fetchStreams(t), interval);

  const fetchData = async () => {
    fetchResolutions();
    systemInfoCapabilities.is2110() ? await fetchEssences(t) : await fetchVideoInputs();
  };

  useEffect(() => {
    fetchData();
  }, []);

  watchEvent('preset-load', () => {
    fetchVideoEncoders(t);
    fetchData();
  });
  // state management ENDs

  const statusTypes: Status[] = [
    { status: 'ENCODING', color: colorValue('haiui-green-01') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const filteredStart = () => videoEncoders.filter((e) => e.selected && encoderIsStopped(e.stats?.state));

  const handleStartEncoder = (): void | Promise<any> => {
    const filtered = filteredStart(); // filtered AND selected

    if (filtered.length > 0) {
      return Promise.all(filtered.map((e) => startStopVideoEncoder(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('videnc.notifications.startError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => videoEncoders.filter((e) => e.selected && encoderIsStarted(e.stats?.state));

  const handleStopEncoder = (): void | Promise<any> => {
    const filtered = filteredStop();

    if (filtered.length > 0) {
      return Promise.all(filtered.map((e) => startStopVideoEncoder(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('videnc.notifications.stopError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const handleSelect = (item: VideoEncoderViewModel, selected: boolean) => {
    const encoders = videoEncoders.map((i: VideoEncoderViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setVideoEncoders(encoders);
  };

  const onSelectAll = (selected: boolean) => {
    const models = videoEncoders.map((i) => ({ ...i, selected }));
    setVideoEncoders(models);
  };

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    interfaceTxt: { title: t('sort.sortText.input'), type: SortType.NATURAL_SORT },
    resolution: {
      title: t('sort.sortText.resolution'),
      type: SortType.NATURAL_SORT,
      provider: (model: VideoEncoderViewModel) => {
        if (model.stats?.resolution == null && model.resolutionText === 'Input/Auto') {
          return '';
        } else if (model.stats?.resolution == null && model.resolutionText && model.resolutionText.length > 0) {
          return model.resolutionText;
        }
        return model.stats?.resolution; // only sent when bInputPresent
      },
    },
    codecAlgorithm: { title: t('sort.sortText.codec'), type: SortType.GENERIC },
    bitrate: { title: t('sort.sortText.bitrate'), type: SortType.GENERIC },
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
  };

  if (constant.product === PRODUCT.FX) {
    delete sortInfo.interfaceTxt;
  }

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:videnc`,
    [],
  );

  return (
    <StreamingActionBar
      items={videoEncoders}
      searchKeys={['name', 'bitrate', 'interfaceTxt', 'codecTxt', 'inputFormat', 'outputResolution']}
      initialSearchFilters={searchFilters}
      isLoading={isLoading}
      onChangeSearchFilters={setSearchFilters}
      sortInfo={sortInfo}
      statusTypes={statusTypes}
      getStateValue={encoderModelToStatus}
      scrollToId={(props as any).scrollToId}
      actionButtons={
        sessionStore.isUser()
          ? []
          : [
              { onClick: handleStartEncoder, counter: filteredStart, label: t('general.start') },
              { onClick: handleStopEncoder, counter: filteredStop, label: t('general.stop') },
            ]
      }
      List={VidencList}
      onSelectAll={onSelectAll}
      updateSelection={handleSelect} //TODO rename onSelect
      storageKey="videnc"
      placeholderThumbnail={true}
      placeholderNumPanels={constant.product === PRODUCT.FX ? 4 : 8}
    />
  );
};
